/**
 * GapChart — F1-style cumulative time delta chart
 *
 * Hero chart on the Race Dynamics tab.
 *
 * Features:
 *   - Single gap line (time_delta_s) vs course distance
 *   - Dual-colored area fill: blue when A is ahead, amber when B is ahead
 *   - Zero line (thick)
 *   - Zero crossing diamond markers (lead changes)
 *   - Right Y-axis: elevation overlay (semi-transparent)
 *   - Checkpoint reference lines with labels
 *   - Linked hover (emits onHover) and zoom (emits onZoomChange)
 *   - Tooltip with Δt, A pace, B pace, elevation
 */

import { useMemo, useCallback } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceDot,
  ReferenceArea,
  Tooltip,
  Legend,
} from 'recharts';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { buildGpsAnchors, buildCombinedCorrection } from '../../../utils/gpsAnchors';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';
const A_FILL = 'rgba(59,130,246,0.22)';   // blue — A ahead (matches COLOR_A)
const B_FILL = 'rgba(245,158,11,0.20)';   // amber — B ahead (matches COLOR_B)

function formatTime(seconds) {
  if (seconds == null) return '—';
  const sign = seconds < 0 ? '−' : '+';
  const abs = Math.abs(seconds);
  const m = Math.floor(abs / 60);
  const s = Math.floor(abs % 60);
  return `${sign}${m}:${s.toString().padStart(2, '0')}`;
}

function formatPace(minPerKm) {
  if (minPerKm == null) return '—';
  const m = Math.floor(minPerKm);
  const s = Math.round((minPerKm - m) * 60);
  return `${m}:${s.toString().padStart(2, '0')} /km`;
}

function formatElapsed(seconds) {
  if (seconds == null) return '—';
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = Math.floor(abs % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Split resampled data into positive (A ahead) and negative (B ahead) segments
// for dual-color area fill. Inserts zero-crossing interpolation points.
function buildFillSegments(resampled) {
  if (!resampled || resampled.length === 0) return { positive: [], negative: [] };

  const positive = resampled.map((row) => ({
    ...row,
    gap_pos: row.time_delta_s > 0 ? row.time_delta_s : 0,
    gap_neg: row.time_delta_s < 0 ? row.time_delta_s : 0,
  }));

  return { all: positive };
}

// Custom tooltip
function GapTooltip({ active, payload, label }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!active || !payload || payload.length === 0) return null;

  const row = payload[0]?.payload;
  if (!row) return null;

  const km = (row.course_distance_m / 1000).toFixed(1);

  return (
    <Box
      sx={{
        backgroundColor: colors.grey[800],
        border: `1px solid ${colors.grey[700]}`,
        borderRadius: 1,
        p: 1.5,
        minWidth: 180,
      }}
    >
      <Typography variant="caption" fontWeight="bold" color={colors.grey[200]}>
        km {km}
      </Typography>
      <Box mt={0.5}>
        <Typography variant="caption" display="block" color={row.time_delta_s >= 0 ? COLOR_A : COLOR_B}>
          Δt: {formatTime(row.time_delta_s)}
        </Typography>
        <Typography variant="caption" display="block" color={COLOR_A}>
          A elapsed: {formatElapsed(row.elapsed_a_s)}
        </Typography>
        <Typography variant="caption" display="block" color={COLOR_B}>
          B elapsed: {formatElapsed(row.elapsed_b_s)}
        </Typography>
        <Typography variant="caption" display="block" color={COLOR_A}>
          A pace: {formatPace(row.pace_a)}
        </Typography>
        <Typography variant="caption" display="block" color={COLOR_B}>
          B pace: {formatPace(row.pace_b)}
        </Typography>
        {row.altitude_a != null && (
          <Typography variant="caption" display="block" color={colors.grey[400]}>
            Elevation: {Math.round(row.altitude_a)} m
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function GapChart({
  resampled = [],
  checkpoints = null,
  hoverDistanceM,
  onHover,
  zoomDomain,
  onZoomChange,
  onCheckpointClick,
  onOpenGhostRace,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  // Build the gap line so it (a) passes through the exact gap_at_cp_s at each checkpoint
  // and (b) treats checkpoint stops the SAME way as the accuracy report — as a single clean
  // step at the checkpoint, not the raw DTW spikes where each runner's stop physically lands
  // on its own GPS track (those don't cancel on length-mismatched courses).
  //
  // Method: draw a stop-free MOVING base line (moving_elapsed_b − moving_elapsed_a), add each
  // checkpoint's stop back as a step at the checkpoint position, and drift-correct to the
  // arrival gaps (which also absorbs scattered, non-checkpoint stops smoothly). Falls back to
  // the plain drift correction on the raw total delta when moving data isn't present.
  // Race checkpoints when they exist; otherwise anchors synthesised from the
  // GPS alignment itself. Real checkpoints always win — they carry an external
  // truth a DTW pairing cannot (see utils/gpsAnchors.js).
  const { anchors, shapeAnchors, anchorsAreSynthetic } = useMemo(() => {
    const real = (checkpoints || []).filter(
      cp => cp.distance_m != null && cp.gap_at_cp_s != null);
    const trackPoints = resampled
      .map(r => ({ distance_m: r.course_distance_m, lat: r.lat_a ?? null, lon: r.lon_a ?? null }));
    const synthetic = buildGpsAnchors(resampled, trackPoints);
    // Checkpoints stay authoritative; the GPS anchors only shape the curve
    // between them (see buildCombinedCorrection).
    if (real.length) {
      return { anchors: real, shapeAnchors: synthetic, anchorsAreSynthetic: false };
    }
    return { anchors: synthetic, shapeAnchors: [], anchorsAreSynthetic: true };
  }, [checkpoints, resampled]);

  const correctedResampled = useMemo(() => {
    if (!anchors.length || !resampled.length) return resampled;

    const validCps = [...anchors].sort((a, b) => a.distance_m - b.distance_m);
    if (!validCps.length) return resampled;

    const lerpFactory = (corrs) => (d) => {
      if (d <= corrs[0].distance_m) return corrs[0].correction;
      const last = corrs[corrs.length - 1];
      if (d >= last.distance_m) return last.correction;
      const ri = corrs.findIndex(c => c.distance_m >= d);
      const l = corrs[ri - 1], r = corrs[ri];
      const t = (d - l.distance_m) / (r.distance_m - l.distance_m);
      return l.correction + t * (r.correction - l.correction);
    };

    const nearestRow = (d) => {
      let best = resampled[0];
      for (const row of resampled)
        if (Math.abs(row.course_distance_m - d) < Math.abs(best.course_distance_m - d)) best = row;
      return best;
    };

    const hasMoving = resampled[0]?.moving_elapsed_a_s != null && resampled[0]?.moving_elapsed_b_s != null;

    if (hasMoving) {
      const movingDelta = (row) => row.moving_elapsed_b_s - row.moving_elapsed_a_s;
      // Net checkpoint-stop difference (B − A) accumulated up to distance d — a step function.
      const stopStep = (d) => {
        let s = 0;
        for (const cp of validCps) { if (cp.distance_m <= d) s += (cp.stop_b_s || 0) - (cp.stop_a_s || 0); else break; }
        return s;
      };
      const stopBefore = (d) => {  // stops strictly before this checkpoint (arrival side)
        let s = 0;
        for (const cp of validCps) { if (cp.distance_m < d - 1) s += (cp.stop_b_s || 0) - (cp.stop_a_s || 0); else break; }
        return s;
      };
      const cpCorrections = validCps.map(cp => {
        const best = nearestRow(cp.distance_m);
        return { distance_m: cp.distance_m, correction: cp.gap_at_cp_s - (movingDelta(best) + stopBefore(cp.distance_m)) };
      });
      // Shape hints from GPS, pinned so every checkpoint stays exact.
      const softCorrections = (shapeAnchors || []).map(a => {
        const best = nearestRow(a.distance_m);
        return { distance_m: a.distance_m,
                 correction: a.gap_at_cp_s - (movingDelta(best) + stopBefore(a.distance_m)) };
      });
      const lerp = softCorrections.length
        ? buildCombinedCorrection(cpCorrections, softCorrections)
        : lerpFactory(cpCorrections);
      return resampled.map(row => {
        const delta = movingDelta(row) + stopStep(row.course_distance_m) + lerp(row.course_distance_m);
        return { ...row, time_delta_s: delta, gap_pos: delta > 0 ? delta : 0, gap_neg: delta < 0 ? delta : 0 };
      });
    }

    // Fallback: plain drift correction on the raw total delta.
    const cpCorrections = validCps.map(cp => ({
      distance_m: cp.distance_m, correction: cp.gap_at_cp_s - nearestRow(cp.distance_m).time_delta_s,
    }));
    const lerp = lerpFactory(cpCorrections);
    return resampled.map(row => {
      const delta = row.time_delta_s + lerp(row.course_distance_m);
      return { ...row, time_delta_s: delta, gap_pos: delta > 0 ? delta : 0, gap_neg: delta < 0 ? delta : 0 };
    });
  }, [resampled, anchors, shapeAnchors]);

  const { all: chartData } = useMemo(() => buildFillSegments(correctedResampled), [correctedResampled]);

  // X-axis domain (km)
  const xDomain = useMemo(() => {
    if (zoomDomain) return zoomDomain.map((v) => v / 1000);
    if (!correctedResampled.length) return [0, 1];
    return [0, correctedResampled[correctedResampled.length - 1].course_distance_m / 1000];
  }, [correctedResampled, zoomDomain]);

  // Elevation Y range for right axis
  const elevRange = useMemo(() => {
    if (!chartData?.length) return [0, 1000];
    const alts = chartData.map((r) => r.altitude_a).filter(Boolean);
    if (!alts.length) return [0, 1000];
    const min = Math.min(...alts);
    const max = Math.max(...alts);
    return [Math.max(0, min - 50), max + 50];
  }, [chartData]);

  const handleMouseMove = useCallback(
    (e) => {
      if (e?.activePayload?.[0]) {
        onHover?.(e.activePayload[0].payload.course_distance_m);
      }
    },
    [onHover]
  );

  const handleMouseLeave = useCallback(() => onHover?.(null), [onHover]);

  if (!chartData || chartData.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography color={colors.grey[500]}>{t('noAlignedData', 'No aligned data available')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1}>
        {t('gapChartTitle', 'Gap Chart — Cumulative Time Delta')}
      </Typography>
      <Typography variant="caption" color={colors.grey[400]} display="block" mb={anchors.length ? 0.5 : 2}>
        {t('gapChartCaption', 'Blue = A ahead · Amber = B ahead · Slope shows who is gaining pace')}
      </Typography>
      {/* Say which construction produced this line. The checkpoint-anchored and
          GPS-anchored curves can differ visibly, and a reader comparing two of
          them deserves to know they were not built the same way. */}
      {anchors.length > 0 && (
        <Typography variant="caption" color={colors.grey[500]} display="block" mb={2}>
          {anchorsAreSynthetic
            ? t('gapAnchoredGps',
                'Anchored at {{n}} well-matched GPS points (no race checkpoints on this comparison) — stops are shown where they happened, and the line between anchors can drift.',
                { n: anchors.length })
            : t('gapAnchoredCheckpoints',
                'Anchored at {{n}} race checkpoints — the line passes through the measured gap at each one{{shape}}.',
                { n: anchors.length,
                  shape: shapeAnchors?.length
                    ? t('gapShapedByGps', ', with the shape between them from {{m}} GPS points',
                        { m: shapeAnchors.length })
                    : '' })}
        </Typography>
      )}

      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart
          data={chartData}
          margin={isMobile ? { top: 55, right: 5, left: 0, bottom: 10 } : { top: 55, right: 60, left: 10, bottom: 10 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />

          {/* X axis: km */}
          <XAxis
            dataKey="course_distance_m"
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}`}
            domain={xDomain.map((v) => v * 1000)}
            type="number"
            tick={{ fill: colors.grey[400], fontSize: 11 }}
            label={{ value: 'km', position: 'insideBottomRight', fill: colors.grey[500], fontSize: 11 }}
          />

          {/* Left Y axis: time delta (seconds → mm:ss) */}
          <YAxis
            yAxisId="gap"
            tickFormatter={(v) => formatTime(v)}
            tick={isMobile ? false : { fill: colors.grey[400], fontSize: 10 }}
            axisLine={!isMobile}
            tickLine={!isMobile}
            width={isMobile ? 0 : 55}
          />

          {/* Right Y axis: elevation */}
          <YAxis
            yAxisId="elev"
            orientation="right"
            domain={elevRange}
            tick={isMobile ? false : { fill: colors.grey[500], fontSize: 10 }}
            axisLine={!isMobile}
            tickLine={!isMobile}
            width={isMobile ? 0 : 45}
            tickFormatter={(v) => `${Math.round(v)}m`}
          />

          {/* Zero line */}
          <ReferenceLine yAxisId="gap" y={0} stroke={colors.grey[300]} strokeWidth={2} />

          {/* Checkpoint reference lines with labels */}
          {checkpoints?.map((cp, i) => (
            <ReferenceLine
              key={i}
              yAxisId="gap"
              x={cp.distance_m}
              stroke={colors.greenAccent[500]}
              strokeDasharray="5 5"
              strokeWidth={2}
              onClick={onCheckpointClick}
              label={{
                value: cp.name,
                position: 'top',
                fill: colors.greenAccent[500],
                fontSize: 11,
                angle: -45,
                offset: 5,
              }}
            />
          ))}

          {/* Elevation area (right axis, background) */}
          <Area
            yAxisId="elev"
            dataKey="altitude_a"
            fill="rgba(100,116,139,0.15)"
            stroke="rgba(100,116,139,0.4)"
            strokeWidth={1}
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />

          {/* Positive gap fill (A ahead = green) */}
          <Area
            yAxisId="gap"
            dataKey="gap_pos"
            fill={A_FILL}
            stroke="none"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />

          {/* Negative gap fill (B ahead = red) */}
          <Area
            yAxisId="gap"
            dataKey="gap_neg"
            fill={B_FILL}
            stroke="none"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />

          {/* Gap line */}
          <Line
            yAxisId="gap"
            dataKey="time_delta_s"
            stroke={colors.grey[100]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: colors.grey[100] }}
            isAnimationActive={false}
          />

          {/* Checkpoint dot markers — gap line already passes through these values */}
          {checkpoints?.filter(cp => cp.distance_m != null && cp.gap_at_cp_s != null).map((cp, i) => {
            const fill = cp.gap_at_cp_s >= 0 ? COLOR_A : COLOR_B;
            const r = 5;
            return (
              <ReferenceDot
                key={`cp-dot-${i}`}
                yAxisId="gap"
                x={cp.distance_m}
                y={cp.gap_at_cp_s}
                shape={(props) => {
                  const { cx, cy } = props;
                  return (
                    <polygon
                      points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`}
                      fill={fill}
                      stroke={colors.grey[900]}
                      strokeWidth={1.5}
                    />
                  );
                }}
              />
            );
          })}

          {/* Linked hover cursor */}
          {hoverDistanceM != null && (
            <ReferenceLine
              yAxisId="gap"
              x={hoverDistanceM}
              stroke={colors.grey[300]}
              strokeWidth={1}
            />
          )}

          <Tooltip content={<GapTooltip />} />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <Box display="flex" gap={3} mt={1} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: A_FILL, border: `1px solid ${COLOR_A}`, borderRadius: 0.5 }} />
          <Typography variant="caption" color={colors.grey[400]}>{t('aAhead', 'A ahead')}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: B_FILL, border: `1px solid ${COLOR_B}`, borderRadius: 0.5 }} />
          <Typography variant="caption" color={colors.grey[400]}>{t('bAhead', 'B ahead')}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <svg width={12} height={12} viewBox="0 0 12 12">
            <polygon points="6,0 12,6 6,12 0,6" fill={colors.grey[300]} stroke="#1a1a2e" strokeWidth="1" />
          </svg>
          <Typography variant="caption" color={colors.grey[400]}>{t('checkpointGap', 'Checkpoint gap (exact)')}</Typography>
        </Box>
      </Box>
    </Box>
  );
}
